import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Form, message } from "antd";
import { bindActionCreators } from "redux";
import { Creators as LocationActions } from "store/location/location-duck";
import { ILocationData, IStructuresAllData } from "store/location/location-model";
import { Creators as ReportActions } from "store/report/report-duck";
import { IItems } from "store/report/report-model";
import { Creators as UserSessionActions } from "store/user-session/user-session-duck";
import { IUserData, MenuItemActive } from "store/user-session/user-session-model";
import TokenService from "utils/auth/Token.service";
import { Roles } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "./../components/styled";
import RenderReport from "./renderReport";
import { onConfirmExportReport, onConfirmQueryReport } from "./report.service";

export interface IReport {
  structure: IStructuresAllData;
  location: ILocationData[];
  rangePicker: string[] | any;
}

interface IPropsScreenReport {
  isLoading?: boolean;
  isLoadingRegisters?: boolean;
  userData?: IUserData;
  structuresAllList?: IStructuresAllData[];
  locationByStructureIdList?: IStructuresAllData[];
  locationStructure?: IStructuresAllData;
  items?: Array<IItems>;
  totalItems?: number;
  pageIndex?: number;
  itemsByPage?: number;
  inboundDocumentsTypes?: string[];
  outboundDocumentsTypes?: string[];
}

export interface IDispatchReportProps {
  locationStructuresAllFetchAsync?: (...args) => void;
  locationByStructureIdFetchAsync?: (...args) => void;
  locationStructureByLocationFetchAsync?: (...args) => void;
  reportFetchAsync?: (...args) => void;
  reportFetchRegisterAsync?: (...args) => void;
  reportChangeFieldValue?: (...args) => void;
  reportFetchRegister?: (...args) => void;
  userSessionMenuItemActive?: (...args) => void;
}

type IProps = IPropsScreenReport & IDispatchReportProps;

const Report = ({
  isLoading,
  isLoadingRegisters,
  userData,
  structuresAllList,
  locationByStructureIdList,
  locationStructure,
  items,
  totalItems,
  pageIndex,
  itemsByPage,
  inboundDocumentsTypes,
  outboundDocumentsTypes,
  reportFetchAsync,
  locationStructuresAllFetchAsync,
  locationByStructureIdFetchAsync,
  locationStructureByLocationFetchAsync,
  reportFetchRegisterAsync,
  reportChangeFieldValue,
  reportFetchRegister,
  userSessionMenuItemActive,
}: IProps) => {
  const tokenService = new TokenService();
  const [formReport] = Form.useForm();
  const FILL_IN_THE_FIELDS_CORRECTLY_MESSAGE = useTranslation({ id: "fill_in_the_fields_correctly" });
  const [structureSelected, setStructureSelected] = useState(null);
  const [value, setValue] = useState([userData?.location]);
  const [structureDefault, setStructureDefault] = useState(locationStructure);
  const [getStructure, setGetStructure] = useState(false);
  const [allowView, setAllowView] = useState(false);
  const [dates, setDates] = useState([]);
  const [dateValue, setDateValue] = useState([]);
  const maxDaysSelect = 31;

  useEffect(() => {
    userSessionMenuItemActive(MenuItemActive.INBOUND_OUTBOUND);
    tokenService.authenticateToken();
  }, []);

  const locationStructuresAllFetchAsyncCallback = useCallback(() => locationStructuresAllFetchAsync(), [locationStructuresAllFetchAsync]);
  useEffect(() => {
    locationStructuresAllFetchAsyncCallback();
  }, []);

  const locationByStructureIdFetchAsyncCallback = useCallback((id) => locationByStructureIdFetchAsync(id), [locationByStructureIdFetchAsync]);
  useEffect(() => {
    if (structureDefault) {
      locationByStructureIdFetchAsyncCallback(structureDefault.id);
    }
  }, [structureDefault]);

  const locationStructureByLocationFetchAsyncCallback = useCallback((location) => locationStructureByLocationFetchAsync(location), [locationStructureByLocationFetchAsync]);
  useEffect(() => {
    if (userData?.location) {
      permissionView();
      locationStructureByLocationFetchAsyncCallback(userData.locationId);
      setGetStructure(true);
    }
  }, []);
  useEffect(() => {
    if (getStructure) {
      setStructureDefault(locationStructure);
      setStructureSelected(locationStructure);
    }
  }, []);
  useEffect(() => {
    return () => {
      reportFetchRegister([], 0, []);
    };
  }, []);

  const onFinish = async () => {
    const structureSelectedId = structureSelected === null ? locationStructure?.id : structureSelected?.id;
    onConfirmExportReport({
      formReport,
      locationStructure: structureSelectedId,
      location: value,
      reportFetchAsync,
      locationByStructureIdList,
      message,
      messageDescription: FILL_IN_THE_FIELDS_CORRECTLY_MESSAGE,
      userData,
    });
    return true;
  };

  const resetStateFilters = () => {
    reportChangeFieldValue("structureId", null);
    reportChangeFieldValue("initialEventTime", null);
    reportChangeFieldValue("finalEventTime", null);
    reportChangeFieldValue("location", null);
    reportChangeFieldValue("pageIndex", 1);
    reportChangeFieldValue("plate", null);
    reportChangeFieldValue("trailerPlate", null);
    reportChangeFieldValue("document", null);
  };

  const onReportQuery = async () => {
    const structureSelectedId = structureSelected === null ? locationStructure?.id : structureSelected?.id;
    resetStateFilters();
    onConfirmQueryReport({
      formReport,
      locationStructure: structureSelectedId,
      location: value,
      reportFetchRegisterAsync,
      locationByStructureIdList,
      message,
      messageDescription: FILL_IN_THE_FIELDS_CORRECTLY_MESSAGE,
      pageIndex,
      itemsByPage,
      userData,
    });
    return true;
  };

  const permissionView = () => {
    const allowedRoles = [Roles.ADMIN, Roles.CONTROL, Roles.CONCIERGE_LEADER, Roles.ATTENDANCE, Roles.CONCIERGE_ATTENDANCE];
    const allow = (Object.values(allowedRoles) as string[]).includes(userData.role);
    return setAllowView(!allow);
  };

  const onChangeStructureSelect = (structure) => {
    setValue([]);
    const objStructure = JSON.parse(structure);
    formReport.setFieldsValue({ structure: objStructure.description });
    locationByStructureIdFetchAsyncCallback(objStructure.id);
    setStructureSelected(objStructure);
  };

  const onOpenChangeDatePicker = (open) => {
    if (open) {
      setDates([]);
      formReport.setFieldsValue({ rangePicker: "" });
    }
  };

  return (
    <>
      <RenderReport
        isLoading={isLoading}
        isLoadingRegisters={isLoadingRegisters}
        userData={userData}
        locationStructure={locationStructure}
        setValue={setValue}
        value={value}
        onFinish={onFinish}
        formReport={formReport}
        locationByStructureIdList={locationByStructureIdList}
        allowView={allowView}
        onChangeStructureSelect={onChangeStructureSelect}
        structuresAllList={structuresAllList}
        dateValue={dateValue}
        onOpenChangeDatePicker={onOpenChangeDatePicker}
        setDateValue={setDateValue}
        setDates={setDates}
        dates={dates}
        maxDaysSelect={maxDaysSelect}
        onReportQuery={onReportQuery}
        items={items}
        totalItems={totalItems}
        reportChangeFieldValue={reportChangeFieldValue}
        reportFetchRegisterAsync={reportFetchRegisterAsync}
        pageIndex={pageIndex}
        inboundDocumentsTypes={inboundDocumentsTypes}
        outboundDocumentsTypes={outboundDocumentsTypes}
      />
      <button data-testid="submit-report" onClick={onFinish} style={S.FakeBtnConfirm} disabled={isLoadingRegisters} />
      <input type="number" id="locationFake" onChange={(e) => formReport.setFieldsValue({ locationId: e.target.value })} style={S.FakeBtnConfirm} />
    </>
  );
};

const mapDispatchToActions = (dispatch: any) => bindActionCreators({ ...UserSessionActions, ...ReportActions, ...LocationActions }, dispatch);

const mapStateToProps = (state) => ({
  isLoading: state.Report.isLoading,
  isLoadingRegisters: state.Report.isLoadingRegisters,
  userData: state.UserSession.userData,
  structuresAllList: state.Location.structuresAllList,
  locationByStructureIdList: state.Location.locationByStructureIdList,
  locationStructure: state.Location.locationStructure,
  items: state.Report.items,
  totalItems: state.Report.totalItems,
  pageIndex: state.Report.filter.pageIndex,
  itemsByPage: state.Report.filter.itemsByPage,
  inboundDocumentsTypes: state.Report.inboundDocumentsTypes,
  outboundDocumentsTypes: state.Report.outboundDocumentsTypes,
});

export default connect(mapStateToProps, mapDispatchToActions)(Report);
